import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CloudflareTurnstile from '../components/CloudflareTurnstile';

import SEO from '../components/SEO';
import '../styles/Auth.css';

const ForgotPasswordPage = () => {
  const { resetPassword, loading: authLoading, error: authError, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => {
    return () => {
      clearError();
      clearInterval(cooldownRef.current);
    };
  }, [clearError]);

  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(cooldownRef.current);
  }, [resendCooldown > 0]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await resetPassword(email);

      if (result.success) {
        setSuccess(true);
        setResendCooldown(30);
      } else {
        setError(result.error || 'Failed to send reset email. Please try again.');
        setTurnstileToken(null);
        setTurnstileResetKey(prev => prev + 1);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setTurnstileToken(null);
      setTurnstileResetKey(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const result = await resetPassword(email);
      if (result.success) {
        setResendCooldown(30);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <SEO title="Reset Password" path="/forgot-password" noindex={true} />
        <div className="auth-left">
          <div className="auth-logo">
            <Link to="/" className="auth-logo-link">
              <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
              <span className="brand-name">pathproxy</span>
            </Link>
          </div>

          <div className="auth-form-container">
            <div className="success-message">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E8FF00" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <h1>Check Your Email</h1>
              <p>We've sent a password reset link to <strong>{email}</strong></p>
              <p className="auth-subtitle">Click the link in the email to reset your password.</p>

              <div className="spam-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>Can't find the email? Please check your <strong>Spam</strong> or <strong>Junk</strong> folder.</span>
              </div>

              <button
                className="resend-btn"
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner"></span>
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend email (${resendCooldown}s)`
                ) : (
                  'Resend reset email'
                )}
              </button>

              <Link to="/login" className="auth-submit-btn" style={{ display: 'inline-block', textDecoration: 'none', marginTop: '12px' }}>
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        <div className="auth-right">
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SEO title="Forgot Password" path="/forgot-password" noindex={true} />
      <div className="auth-left">
        <div className="auth-logo">
          <Link to="/" className="auth-logo-link">
            <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
            <span className="brand-name">pathproxy</span>
          </Link>
        </div>

        <div className="auth-form-container">
          <h1>Forgot Password</h1>
          <p className="auth-subtitle">Enter your email address and we'll send you a reset link</p>
          <p className="auth-switch">
            Remember your password? <Link to="/login">Login</Link>
          </p>

          {(error || authError) && (
            <div className="auth-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {error || authError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <div className="input-with-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="Enter your email address"
                  required
                  autoComplete="email"
                  maxLength={254}
                  disabled={loading || authLoading}
                />
              </div>
            </div>

            <div className="captcha-box">
              <CloudflareTurnstile
                onVerify={(token) => { setTurnstileToken(token); setError(''); }}
                onError={() => { setTurnstileToken(null); setError('Verification failed. Please try again.'); }}
                onExpire={() => setTurnstileToken(null)}
                theme="dark"
                resetKey={turnstileResetKey}
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading || authLoading || !turnstileToken}
            >
              {loading || authLoading ? (
                <>
                  <span className="btn-spinner"></span>
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        </div>
      </div>

      <div className="auth-right">
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
